import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DayTrackMap from '../components/DayTrackMap';
import DayActivityStrip from '../components/DayActivityStrip';
import DayConnectivityStrip from '../components/DayConnectivityStrip';
import GogglesConnectivityTimeline from '../components/GogglesConnectivityTimeline';
import Timeline from '../components/Timeline';
import Sidebar from '../components/Sidebar';
import TelemetryPanel from '../components/TelemetryPanel';
import TelemetryIcon from '../components/icons/TelemetryIcon';
import Button from '../components/Button';
import { fetchUserProfile, getRecordedDayLocations } from '../services/usersService';
import { fetchSessionEventsBetween } from '../services/analyticsService';
import { deriveGogglesConnectivityTimeline } from '../utils/gogglesConnectivityTimeline';
import { deriveDayActivity } from '../utils/dayActivity';
import { dayConnectivitySpan } from '../utils/dayConnectivity';
import { getBrowserTimeZone, timeZoneLabel } from '../utils/timezone';
import { findLocationIndex, formatRecordedDay, nextPlaybackTime } from '../utils/recordedDayTrack';
import { useError } from '../contexts/ErrorContext';
import './Dataset1Page.css';
import './RecordedDayMapPage.css';

const TICK_MS = 100;

/** Seconds of the day played per second of watching. */
const PLAYBACK_SPEEDS = [10, 60, 300, 1200];
const DEFAULT_SPEED = 60;

/** The connectivity panel was laid out for a page, so its tab opens the sidebar wider. */
const CONNECTIVITY_SIDEBAR_WIDTH = 560;

const SIDEBAR_TABS = [
  { id: 'telemetry', label: 'Telemetry', icon: <TelemetryIcon size={24} /> },
  {
    id: 'connectivity',
    label: 'Goggles connectivity',
    icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6.5 6.5 17.5 17.5 12 23 12 1 17.5 6.5 6.5 17.5"/></svg>
  }
];

/**
 * One recorded day merged into a single map and player. The merge happens per
 * request and is never stored, so opening a day creates no dataset. Goggles
 * connectivity comes from the app sessions overlapping the recording, and
 * loads on its own, so the map never waits for it.
 */
const RecordedDayMapPage = () => {
  const navigate = useNavigate();
  const { userId, day } = useParams();
  const { addError } = useError();
  const [recording, setRecording] = useState(null);
  const [connectivity, setConnectivity] = useState({ status: 'idle' });
  const [loadError, setLoadError] = useState(null);
  const [userName, setUserName] = useState(null);
  const [currentTime, setCurrentTime] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [speed, setSpeed] = useState(DEFAULT_SPEED);
  const [activeTab, setActiveTab] = useState('telemetry');
  const [sidebarWidth, setSidebarWidth] = useState(350);
  const stepRef = useRef(DEFAULT_SPEED * (TICK_MS / 1000));

  const locations = useMemo(() => recording?.locations ?? [], [recording]);
  const activity = useMemo(() => deriveDayActivity(locations, recording?.features), [locations, recording]);

  useEffect(() => {
    let cancelled = false;
    setRecording(null);
    setLoadError(null);
    setIsPlaying(false);

    getRecordedDayLocations(userId, day, addError)
      .then((result) => {
        if (cancelled) return;
        setRecording(result);
        setCurrentTime(result.locations[0]?.timestamp ?? null);
      })
      .catch((error) => {
        if (!cancelled) setLoadError(error.message || 'Failed to load this recorded day.');
      });

    return () => {
      cancelled = true;
    };
  }, [userId, day, addError]);

  useEffect(() => {
    let cancelled = false;
    fetchUserProfile(userId)
      .then((profile) => {
        if (!cancelled) setUserName(profile?.name || null);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const trackStartMs = locations.length > 0 ? locations[0].timestamp * 1000 : null;
  const trackEndMs = locations.length > 0 ? locations[locations.length - 1].timestamp * 1000 : null;

  useEffect(() => {
    if (trackStartMs === null) return void 0;
    let cancelled = false;
    setConnectivity({ status: 'loading' });

    fetchSessionEventsBetween(userId, trackStartMs, trackEndMs)
      .then(({ sessions, events, failedSessionCount }) => {
        if (cancelled) return;
        setConnectivity({
          status: 'ready',
          data: deriveGogglesConnectivityTimeline(events),
          span: dayConnectivitySpan(sessions, trackStartMs, trackEndMs),
          failedSessionCount
        });
      })
      .catch((error) => {
        if (!cancelled) setConnectivity({ status: 'error', message: error.message });
      });

    return () => {
      cancelled = true;
    };
  }, [userId, trackStartMs, trackEndMs]);

  const currentIndex = useMemo(
    () => (locations.length > 0 && currentTime !== null ? findLocationIndex(locations, currentTime) : 0),
    [locations, currentTime]
  );
  const currentLocation = locations[currentIndex] ?? null;

  const statsHistory = useMemo(() => {
    if (locations.length === 0) return [];
    const seen = new Set();
    const history = [{ timestamp: locations[0].timestamp, maxVelocitymps: 0, totVerticalMeters: 0 }];
    locations.forEach(({ stats }) => {
      if (stats && !seen.has(stats.timestamp)) {
        seen.add(stats.timestamp);
        history.push(stats);
      }
    });
    return history;
  }, [locations]);

  useEffect(() => {
    stepRef.current = speed * (TICK_MS / 1000);
  }, [speed]);

  useEffect(() => {
    if (!isPlaying) return void 0;
    const interval = setInterval(() => {
      setCurrentTime((time) => nextPlaybackTime(locations, time, stepRef.current));
    }, TICK_MS);
    return () => clearInterval(interval);
  }, [isPlaying, locations]);

  const endTime = locations[locations.length - 1]?.timestamp;

  useEffect(() => {
    if (isPlaying && currentTime >= endTime) setIsPlaying(false);
  }, [isPlaying, currentTime, endTime]);

  const handlePlayPause = () => {
    if (!isPlaying && currentTime >= endTime) setCurrentTime(locations[0].timestamp);
    setIsPlaying(!isPlaying);
  };

  const handleTimeChange = (time) => {
    setCurrentTime(time);
    setIsPlaying(false);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'connectivity') setSidebarWidth((width) => Math.max(width, CONNECTIVITY_SIDEBAR_WIDTH));
  };

  const dayLabel = formatRecordedDay(day);
  // Labelled for the recording's date, so an April day reads PDT even in winter.
  const timeZone = getBrowserTimeZone();
  const timeZoneText = trackStartMs === null ? null : `Times in ${timeZoneLabel(timeZone, new Date(trackStartMs))}`;
  const loading = !recording && !loadError;

  return (
    <div className="dataset1-page">
      <header className="dataset1-header">
        <div className="dataset1-header-content">
          <Button variant="outline" size="small" onClick={() => navigate(`/users/${userId}?tab=recorded-days`)}>
            ← Back to Recorded Days
          </Button>
          <h1>{userName ? `${userName} · ${dayLabel}` : dayLabel}</h1>
          {locations.length > 0 && (
            <span className="recorded-day-map-meta">
              {locations.length.toLocaleString()} points · {recording.fileCount} files
            </span>
          )}
          {recording?.failedFileCount > 0 && (
            <span className="recorded-day-map-warning">
              {recording.failedFileCount} of {recording.fileCount} files could not be read
            </span>
          )}
        </div>
      </header>
      <main className="dataset1-main">
        <div className="dataset1-map-container">
          {/* Mounted before the data arrives, so the map loads alongside it */}
          <DayTrackMap locations={locations} currentIndex={currentIndex} rightInset={sidebarWidth} />
          {locations.length > 0 ? (
            <>
              <div className="recorded-day-map-overlay" style={{ maxWidth: `calc(100% - ${sidebarWidth + 32}px)` }}>
                <div className="recorded-day-map-speeds" role="group" aria-label="Playback speed">
                  {PLAYBACK_SPEEDS.map((option) => (
                    <button
                      key={option}
                      type="button"
                      className={`recorded-day-map-speed${option === speed ? ' active' : ''}`}
                      aria-pressed={option === speed}
                      onClick={() => setSpeed(option)}
                    >
                      {option}×
                    </button>
                  ))}
                </div>
                <div className="recorded-day-map-timezone">{timeZoneText}</div>
              </div>
              <Timeline
                locations={locations}
                currentTime={currentTime}
                onTimeChange={handleTimeChange}
                isPlaying={isPlaying}
                onPlayPause={handlePlayPause}
                sidebarOpen={activeTab !== null}
                sidebarWidth={sidebarWidth}
                timeZone={timeZone}
              >
                <DayActivityStrip
                  stretches={activity}
                  trackStartMs={trackStartMs}
                  trackEndMs={trackEndMs}
                  currentMs={currentTime * 1000}
                />
                {connectivity.data && (
                  <DayConnectivityStrip
                    data={connectivity.data}
                    trackStartMs={trackStartMs}
                    trackEndMs={trackEndMs}
                    currentMs={currentTime * 1000}
                  />
                )}
              </Timeline>
            </>
          ) : (
            <div className={`recorded-day-map-status${loadError ? ' error' : ''}`}>
              {loading ? 'Loading…' : loadError || 'No recorded data for this day.'}
            </div>
          )}
        </div>
        {locations.length > 0 && (
          <Sidebar
            tabs={SIDEBAR_TABS}
            activeTab={activeTab}
            onTabChange={handleTabChange}
            width={sidebarWidth}
            onWidthChange={setSidebarWidth}
            topOffset={50}
          >
            {activeTab === 'telemetry' && (
              <TelemetryPanel currentLocation={currentLocation} statsHistory={statsHistory} currentTime={currentTime} timeZone={timeZone} />
            )}
            {activeTab === 'connectivity' && (
              <div className="recorded-day-map-connectivity">
                {connectivity.status === 'loading' && <p>Loading goggles connectivity…</p>}
                {connectivity.status === 'error' && <p className="error">{connectivity.message}</p>}
                {connectivity.status === 'ready' && !connectivity.data && <p>No goggles activity in this day&apos;s app sessions.</p>}
                {connectivity.failedSessionCount > 0 && (
                  <p className="warning">
                    {connectivity.failedSessionCount} app session{connectivity.failedSessionCount === 1 ? '' : 's'} could not be loaded
                  </p>
                )}
                {connectivity.data && <p className="recorded-day-map-panel-timezone">{timeZoneText}</p>}
                {connectivity.data && (
                  <GogglesConnectivityTimeline
                    data={connectivity.data}
                    sessionStartTime={connectivity.span.startMs}
                    sessionEndTime={connectivity.span.endMs}
                    timeZone={timeZone}
                  />
                )}
              </div>
            )}
          </Sidebar>
        )}
      </main>
    </div>
  );
};

export default RecordedDayMapPage;
